/**
 * Shared selection context for Overview → source drilldown (#113 plumbing; #114 consumes).
 * Synced via hash query alongside desk knobs (#112): well, month, scenario, concern.
 */

import { encodeDeskQuery, loadDeskKnobsForNav, splitHash } from '../engine/deskPersistence.js';

export const SELECTION_QUERY_KEYS = {
  selectedWellId: 'well',
  selectedMonth: 'month',
  scenarioId: 'scenario',
  concernId: 'concern',
};

const SELECTION_FIELDS = ['selectedWellId', 'selectedMonth', 'scenarioId', 'concernId'];

function clean(value) {
  if (value == null) return '';
  const s = String(value).trim();
  return s;
}

/** Parse selection fields from a hash (or hash fragment with query). */
export function parseSelectionContext(rawHash = typeof window !== 'undefined' ? window.location.hash : '') {
  const { path, query } = splitHash(rawHash);
  const params = new URLSearchParams(query);
  // Also accept window search if hash has no selection (rare deep-links).
  if (typeof window !== 'undefined' && !query) {
    const search = window.location.search?.replace(/^\?/, '') || '';
    if (search) {
      const sp = new URLSearchParams(search);
      for (const [, qk] of Object.entries(SELECTION_QUERY_KEYS)) {
        if (!params.has(qk) && sp.has(qk)) params.set(qk, sp.get(qk));
      }
    }
  }
  return {
    path,
    selectedWellId: clean(params.get(SELECTION_QUERY_KEYS.selectedWellId)),
    selectedMonth: clean(params.get(SELECTION_QUERY_KEYS.selectedMonth)),
    scenarioId: clean(params.get(SELECTION_QUERY_KEYS.scenarioId)),
    concernId: clean(params.get(SELECTION_QUERY_KEYS.concernId)),
  };
}

/** Encode selection-only query string (omit empties). */
export function encodeSelectionQuery(selection = {}) {
  const params = new URLSearchParams();
  for (const field of SELECTION_FIELDS) {
    const qk = SELECTION_QUERY_KEYS[field];
    const v = clean(selection[field]);
    if (v) params.set(qk, v);
  }
  return params.toString();
}

/**
 * Merge desk knobs + selection into one query string.
 * Desk numeric keys and selection keys are disjoint by design.
 */
export function mergeHashQuery({ deskKnobs, selection, existingQuery = '' } = {}) {
  const params = new URLSearchParams(existingQuery || '');
  const deskQ = encodeDeskQuery(deskKnobs || {});
  if (deskQ) {
    const deskParams = new URLSearchParams(deskQ);
    for (const [k, v] of deskParams.entries()) params.set(k, v);
  }
  const selQ = encodeSelectionQuery(selection || {});
  if (selQ) {
    const selParams = new URLSearchParams(selQ);
    for (const [k, v] of selParams.entries()) params.set(k, v);
  } else if (selection && typeof selection === 'object') {
    // Explicit clear: empty selection fields remove prior selection keys.
    for (const field of SELECTION_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(selection, field) && !clean(selection[field])) {
        params.delete(SELECTION_QUERY_KEYS[field]);
      }
    }
  }
  return params.toString();
}

/**
 * Build hash path+query for navigation with optional selection + desk knobs.
 * @param {string} dashKey
 * @param {{ selection?: object, knobs?: object, preserveExisting?: boolean }} [opts]
 */
export function hashWithContext(dashKey, opts = {}) {
  const path = String(dashKey || 'overview').replace(/^#/, '').split('?')[0];
  const existing =
    opts.preserveExisting !== false && typeof window !== 'undefined'
      ? splitHash(window.location.hash).query
      : '';
  const knobs =
    opts.knobs ||
    (typeof window !== 'undefined' ? loadDeskKnobsForNav() : {});
  const q = mergeHashQuery({
    deskKnobs: knobs,
    selection: opts.selection,
    existingQuery: existing,
  });
  // When selection provided, overlay it on existing (mergeHashQuery already set keys).
  if (opts.selection) {
    const params = new URLSearchParams(q);
    for (const field of SELECTION_FIELDS) {
      const v = clean(opts.selection[field]);
      const qk = SELECTION_QUERY_KEYS[field];
      if (v) params.set(qk, v);
      else if (Object.prototype.hasOwnProperty.call(opts.selection, field)) params.delete(qk);
    }
    const finalQ = params.toString();
    return finalQ ? `${path}?${finalQ}` : path;
  }
  return q ? `${path}?${q}` : path;
}

/** Serialize selection object for tests / round-trip. */
export function serializeSelectionContext(selection = {}) {
  return encodeSelectionQuery(selection);
}

/**
 * Map a brief concern/unknown item to a source dashboard key.
 */
export function sourceTabForItem(item) {
  if (!item) return 'diligence';
  if (item.sourceTab) return item.sourceTab;
  const cite = Array.isArray(item.citations) ? item.citations[0] : null;
  if (cite?.sourceTab) return cite.sourceTab;
  return 'diligence';
}
